<template>
  <div class="knowledge-exam-student-detail-page">
    <title-bar title="答题表现" back>
      <el-button type="primary" v-if="!isEnd" @click="remindStudent">提醒学生</el-button>
    </title-bar>

    <div class="main">
      <training-overview-info v-if="exam" :training="exam" :format="formatExamTime"></training-overview-info>
      <training-clazz-static-info
        :value="currentClazzId"
        :clazz-options="clazzOption"
        :clazz-summary-items="clazzSummaryInfo"
        @change="updateClazzId"
      ></training-clazz-static-info>
      <list-page class="list-page" :data="mapItems" :limit="query.limit">
        <el-table-column label="学生姓名">
          <template v-slot="{ row }">
            {{ row.studentName }}
          </template>
        </el-table-column>

        <el-table-column label="性别">
          <template v-slot="{ row }">
            {{ row.gender }}
          </template>
        </el-table-column>

        <el-table-column v-if="isEnd" label="正确">
          <template v-slot="{ row }">
            {{ row.correct }}
          </template>
        </el-table-column>

        <el-table-column v-if="isEnd" label="得分">
          <template v-slot="{ row }">
            <div class="score" :class="[calcScoreStatus(row.score)]" v-if="isEnd">
              {{ row.score }}
            </div>
          </template>
        </el-table-column>

        <el-table-column v-if="!isEnd" label="完成进度">
          <template v-slot="{ row }">
            {{ row.finish_progress }}
          </template>
        </el-table-column>

        <el-table-column v-if="!isEnd" label="状态">
          <template v-slot="{ row }">
            <div class="completion-status">
              <div :class="['status', statusClass(row.status)]"></div>
              {{ row.status }}
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="action" label="更多操作" align="center">
          <template v-slot="{ row }">
            <el-button type="text" @click="navToStudentExamResultDetail(row)">详情</el-button>
          </template>
        </el-table-column>
      </list-page>
    </div>
  </div>
</template>
<script lang="ts">
import { BasePage, Component, Prop, Watch } from 'kuaidong-component'
import TitleBar from '@/components/TitleBar.vue'
import TrainingOverviewInfo from '@/views/special-skill/components/TrainingOverviewInfo.vue'
import TrainingClazzStaticInfo from '@/views/special-skill/components/TrainingClazzStaticInfo.vue'
import ListPage from '@/components/ListPage.vue'
import { HomeworkClazzStat, IExamStudentStaticInfo, IExamStudentStaticItem } from 'kuaidong-model'
import { Emit } from 'vue-property-decorator'
import { AWinter2023 } from 'kuaidong-api'
import { DateWrapper, Debounce, UnitUtil } from 'kuaidong-lib'

@Component({
  components: {
    TitleBar,
    TrainingOverviewInfo,
    TrainingClazzStaticInfo,
    ListPage,
  },
})
export default class KnowledgeExamStudentDetailPage extends BasePage {
  @Prop({
    type: String,
    required: true,
  })
  examId!: string

  @Prop({
    type: Number,
    required: true,
  })
  classId!: number

  @Prop({
    type: Array,
    required: true,
  })
  clazzOption!: {
    label: string
    value: number
  }[]

  query = {
    limit: 20,
  }

  clazzPickerVisible = false

  actions = [
    {
      label: '查看题目',
      action: 'examDetail',
    },
  ]

  studentStatisticInfo: IExamStudentStaticInfo | null = null

  currentClazzId = this.classId

  get exam() {
    if (this.studentStatisticInfo) {
      return {
        name: this.studentStatisticInfo.exam.title,
        ...this.studentStatisticInfo.exam,
      }
    } else {
      return null
    }
  }
  get summaryInfo() {
    if (this.studentStatisticInfo) {
      return this.studentStatisticInfo.class_total_summary
    } else {
      return null
    }
  }
  get items() {
    if (this.studentStatisticInfo) {
      return this.studentStatisticInfo.student_summary_list
    } else {
      return []
    }
  }

  get mapItems() {
    if (!this.isEnd) {
      return this.items.map((item) => {
        return {
          ...item,
          studentName: this._show_student_name(item.student_vo as any),
          gender: item.student_vo.gender === 'M' ? '男' : '女',
          finish_progress: `${item.answer_count} / ${item.question_count}`,
          status: this.formatPaperStatus(item.paper_status),
        }
      })
    } else {
      return this.items.map((item) => {
        return {
          ...item,
          studentName: this._show_student_name(item.student_vo as any),
          gender: item.student_vo.gender === 'M' ? '男' : '女',
          correct: item.correct_quantity,
          score: item.score,
        }
      })
    }
  }

  get clazzSummaryInfo() {
    if (!this.isEnd) {
      return [
        {
          value: this.summaryInfo?.students_count,
          title: '学生人数',
        },
        {
          value: this.summaryInfo?.attending_count,
          title: '进行中',
        },
        {
          value: this.summaryInfo?.un_start_count,
          title: '未开始',
        },
        {
          value: this.summaryInfo?.avg_score,
          title: '平均分',
        },
      ]
    } else {
      return [
        {
          value: this.summaryInfo?.students_count,
          title: '学生人数',
        },
        {
          value: this.summaryInfo?.completion_count,
          title: '已完成',
        },
        {
          value: this.summaryInfo?.un_start_count,
          title: '未开始',
        },
        {
          value: this.summaryInfo ? UnitUtil.toFixed(+this.summaryInfo.avg_score, 2) : undefined,
          title: '平均分',
        },
      ]
    }
  }

  get isEnd() {
    return this.exam && this.exam.status === 'END'
  }

  async fetchStudentStaticInfo() {
    this.studentStatisticInfo = await AWinter2023.examStatisticStudentSummary(this.examId, this.currentClazzId)
  }
  calcScoreStatus(score: number) {
    if (score <= 20) {
      return 'failed'
    } else if (score <= 50) {
      return 'not-good'
    } else {
      return 'pass'
    }
  }

  statusClass(status: string) {
    if (status === '未完成' || status === '未开始') {
      return 'un-finished'
    } else if (status === '已完成') {
      return 'finished'
    } else if (status === '进行中') {
      return 'in-process'
    } else {
      return ''
    }
  }

  formatExamTime(dt: string) {
    return new DateWrapper(dt).showStrFullDate
  }

  formatPaperStatus(status: 0 | 1 | 2) {
    if (status === 0) {
      return '未开始'
    } else if (status === 1) {
      return '进行中'
    } else {
      return '已完成'
    }
  }

  formatDate(dt: string) {
    return `${new DateWrapper(dt).showDateStrShort3}  ${new DateWrapper(dt).showTimeStr}`
  }

  @Emit('navToDetail')
  handleClick(item: HomeworkClazzStat) {
    return item
  }

  handleAction() {
    this.navToExamDetail()
  }

  navToExamDetail() {
    this.$router.push({
      name: 'winter_2023_exam_detail',
      query: {
        id: this.exam?.paper_id,
      },
    })
  }

  navToStudentExamResultDetail(item: IExamStudentStaticItem & { status: string }) {
    if (item.status !== '已完成' && !this.isEnd) {
      this.$message.warning('用户答题记录还未生成，请稍后再试')
    } else {
      this.$router.push({
        name: 'pe_knowledge_exam_result_student',
        query: {
          onlineExamClassId: item.online_exam_class_id,
          userId: item.student_vo.user_id,
          clazzName: item.class_vo.note_name || item.class_vo.name,
          title: this.exam?.title,
        },
      })
    }
  }

  @Debounce()
  async remindStudent() {
    try {
      await AWinter2023.onlineExamRemind(this.exam?.online_exam_id)
      this.$message.success('已提醒学生~')
    } catch (e) {
      this.$message.error(e.data)
    }
  }

  updateClazzId(clazzId: number) {
    this.currentClazzId = clazzId
    this.clazzPickerVisible = false
  }

  @Watch('currentClazzId', { immediate: true })
  updateStudentStaticInfo() {
    this.fetchStudentStaticInfo()
  }
}
</script>
<style lang="less">
.knowledge-exam-student-detail-page {
  > .main {
    padding: 22px 24px;
    background: #fff;

    > .list-page {
      margin-top: 24px;
      padding: 0;

      > .el-table {
        > .el-table__body-wrapper {
          > .el-table__body {
            > tbody {
              > .el-table__row {
                > td {
                  > .cell {
                    > .completion-status {
                      display: flex;
                      align-items: center;
                      > .status {
                        width: 6px;
                        height: 6px;
                        border-radius: 50%;
                        margin-right: 4px;
                        &.un-finished {
                          background: #f24b53;
                        }
                        &.finished {
                          background: #3bb528;
                        }
                        &.in-process {
                          background: #3366ff;
                        }
                      }
                    }

                    > .score {
                      &.failed {
                        color: #f24b53;
                      }
                      &.not-good {
                        color: #fe9647;
                      }
                      &.pass {
                        color: #3bb528;
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>
